// vim: tabstop=8 softtabstop=0 noexpandtab shiftwidth=8 nosmarttab

import * as oauth2 from 'oauth4webapi';
import { decodeJwt } from 'jose';
import * as z from "zod";
import { throttle } from 'throttle';
import {
	type DeviceRegistration,
	type DeviceState,
	type DeviceStateBase,
	type DeviceStatusBase,
	type ScreenDetailed as DeviceScreenDetailed,
	type CreateDeviceRequest,
	CreateDeviceResponseSchema,
	type UpdateDeviceStateRequest,
	type UpdateDeviceStatusRequest,
	GetDeviceStateResponseSchema,
} from '@dsbunny/rmm-schema';
import { BunnyBase } from "./bunny-base.js";
import { BunnyAgent } from './bunny-agent.js';
import { BunnyAgentImpl, BunnyAgentImplFactory } from './bunny-agent-impl.js';
import { BunnyAgentRenderer } from './bunny-agent-renderer.js';
import { DesiredStateEventSource } from "./desired-state-event-source.js";
import { TokenSet } from './token-set.js';
import { DeviceFlowHandle } from './device-flow-handle.js';
import {
	RMM_BASE_URL,
	RMM_SCOPE,
	RMM_AUDIENCE,
	ENCLAVE_BASE_URL,
	ENABLE_ENCLAVE,
} from "./config.js";
import { Cool } from './cool.js';

export interface BunnyDeviceSettings {
	issuer_url: string;
}

export class RmmNotReachableError extends Error {
	constructor(public readonly message: string) {
		super(message);
		this.name = 'RmmNotReachableError';
	}
}

export class EnclaveNotReachableError extends Error {
	constructor(public readonly message: string) {
		super(message);
		this.name = 'EnclaveNotReachableError';
	}
}

export class TimeNotInSyncError extends Error {
	constructor(public readonly message: string) {
		super(message);
		this.name = 'TimeNotInSyncError';
	}
}

export class Oauth2DiscoveryError extends Error {
	constructor(public readonly message: string) {
		super(message);
		this.name = 'Oauth2DiscoveryError';
	}
}

export class RefreshTokenForbiddenError extends Error {
	constructor(public readonly message: string) {
		super(message);
		this.name = 'RefreshTokenForbiddenError';
	}
}

export abstract class BunnyDevice extends BunnyBase {
	static URN: string = 'urn:dsbunny:device:default';
	readonly issuer: URL;
	readonly vendorWebGL?: string;
	readonly rendererWebGL?: string;
	// Identity
	protected _cached_device_id?: string | null;
	// OAuth2 client
	protected _cached_client?: any;
	// State
	protected _cached_access_token?: string | null;
	protected _cached_refresh_token?: string | null;
	protected _cached_agent_ids_as_text?: string | null;
	protected _cached_is_maintenance?: boolean | null;
	protected _cached_is_refresh_token_secured?: boolean | null;
	// Status
	protected _cool = new Cool(this.startTimestamp)
	protected _polling: boolean = false;
	protected _has_error: boolean = false;
	protected _error_stack: any | null = null;

	abstract get tags(): string[];

	get _storageKeyPrefix(): string {
		return `device`;
	}

	constructor(options: BunnyDeviceSettings) {
		super();
		this.issuer = new URL(options.issuer_url);
		// Event source
		this._throttle_desired_state = throttle(async () => {
			try {
				if(this._polling) {
					return;
				}
				this._polling = true;
				await this._poll();
				if(this._has_error) {
					this._has_error = false;
					this._error_stack = null;
				}
			} catch(err: unknown) {
				console.warn(`poll: ${
					(err instanceof z.ZodError)
					? JSON.stringify({
						name: 'ZodError',
						detail: err.issues,
						stack: err.stack,
						cause: err.cause,
					})
					: (err instanceof Error)
					? JSON.stringify({
						name: err.name,
						detail: err.message,
						stack: err.stack,
						cause: err.cause,
					})
					: JSON.stringify({ err })
				}`);

				if(err instanceof TimeNotInSyncError) {
					window.dsbunny.rmm.error_code = 'time_not_in_sync';
					window.dsbunny.rmm.state = 'error';
				}
				if(err instanceof EnclaveNotReachableError) {
					window.dsbunny.rmm.error_code = 'enclave_not_reachable';
					if(!this.hasDeviceId()) {
						window.dsbunny.rmm.state = 'error';
					}
				}
				if(err instanceof RmmNotReachableError) {
					window.dsbunny.rmm.error_code = 'rmm_not_reachable';
					if(!this.hasDeviceId()) {
						window.dsbunny.rmm.state = 'error';
					}
				}
				if(err instanceof Oauth2DiscoveryError) {
					window.dsbunny.rmm.error_code = 'oauth2_discovery';
					if(!this.hasDeviceId()) {
						window.dsbunny.rmm.state = 'error';
					}
				}
				if(err instanceof RefreshTokenForbiddenError) {
					console.warn('Token forbidden, clearing local storage');
					this._revokeState();
				}

				this._cool.outage();
				this._has_error = true;
				this._error_stack = (err instanceof Error) ? err.stack : null;
			} finally {
				this._polling = false;
			}
		}, 1000);
		// Status detail
		const { vendor, renderer } = this._queryWebGL();
		if(typeof vendor === "string" && typeof renderer === "string") {
			this.vendorWebGL = vendor;
			this.rendererWebGL = renderer;
		}
	}

	_queryWebGL(): { vendor?: string, renderer?: string } {
		const canvas = document.createElement('canvas');
		const gl = canvas.getContext('webgl');
		if(gl === null) {
			return { vendor: undefined, renderer: undefined }
		}
		const debugInfo = gl.getExtension("WEBGL_debug_renderer_info");
		if(debugInfo === null) {
			return { vendor: undefined, renderer: undefined }
		}
		const vendor = gl.getParameter(debugInfo.UNMASKED_VENDOR_WEBGL);
		const renderer = gl.getParameter(debugInfo.UNMASKED_RENDERER_WEBGL);
		return { vendor, renderer };
	}

	abstract clientId(): Promise<string>;

	getDeviceId(): string | null {
		return this._readDeviceId();
	}

	hasDeviceId(): boolean {
		return (this._readDeviceId() !== null);
	}

	// Clear all state, including device ID and tokens.
	_revokeState(): void {
		this._deleteDeviceId();
		this._deleteTokenSet();
		this._deleteDesiredState();
		this._deleteIsMaintenance();
		this._deleteIsRefreshTokenSecured();
		window.dsbunny.rmm.state = 'revoked';
	}

	// Verify that the authentication server is reachable.
	// This does not verify that the device is registered or authorized.
	// Returns `{ is_reachable: true }` if reachable,
	//  `{ is_reachable: false }` otherwise.
	async verifyAuthenticationServer(): Promise<{
		is_reachable: boolean,
	}> {
		try {
			await this._authorizationServer();
			return { is_reachable: true };
		} catch(err: unknown) {
			console.warn(`verifyAuthenticationServer: ${
				(err instanceof Error) ? JSON.stringify({
					name: err.name,
					message: err.message,
					stack: err.stack,
					cause: err.cause,
				}) : JSON.stringify({ err })
			}`);
			return { is_reachable: false };
		}
	}

	// Verify that the Secure Enclave is reachable.
	async verifyEnclave(): Promise<{
		is_reachable: boolean,
	}> {
		try {
			const url = `${ENCLAVE_BASE_URL}/api/v1/ping`;
			const response = await fetch(url, {
				method: 'GET',
				cache: 'no-store',
			});
			if(!response.ok) {
				return { is_reachable: false };
			}
			const json = await response.json();
			const result = z.object({
				ping: z.literal('pong'),
				timestamp: z.iso.datetime(),
			}).parse(json);
			return { is_reachable: (result.ping === 'pong') };
		} catch(err: unknown) {
			console.warn(`verifyEnclave: ${
				(err instanceof Error) ? JSON.stringify({
					name: err.name,
					message: err.message,
					stack: err.stack,
					cause: err.cause,
				}) : JSON.stringify({ err })
			}`);
			return { is_reachable: false };
		}
	}

	// Verify that the RMM server is reachable.
	// This does not verify that the device is registered or authorized.
	// Returns `{ is_reachable: true, is_time_in_sync: true }` if reachable and time in sync,
	//  `{ is_reachable: true, is_time_in_sync: false }` if reachable but time not in sync,
	//  `{ is_reachable: false }` otherwise.
	async verifyRmmServer(): Promise<{
		is_reachable: boolean,
		is_time_in_sync?: boolean,
	}> {
		try {
			const url = `${RMM_BASE_URL}/api/v1/ping`;
			const response = await fetch(url, {
				method: 'GET',
				cache: 'no-store',
			});
			if(!response.ok) {
				return { is_reachable: false };
			}
			const json = await response.json();
			const result = z.object({
				ping: z.literal('pong'),
				timestamp: z.iso.datetime(),
			}).parse(json);
			const timestampDelta = Math.abs((new Date(result.timestamp)).getTime() - (new Date()).getTime());
			return (timestampDelta < 5 * 60 * 1000)
				? { is_reachable: true, is_time_in_sync: true }
				: { is_reachable: true, is_time_in_sync: false }; // 5 minutes
		} catch(err: unknown) {
			console.warn(`verifyRmmServer: ${
				(err instanceof Error) ? JSON.stringify({
					name: err.name,
					message: err.message,
					stack: err.stack,
					cause: err.cause,
				}) : JSON.stringify({ err })
			}`);
			return { is_reachable: false };
		}
	}

	// Ensure the device is registered and return the device ID.
	async _readOrRegisterDeviceId(): Promise<string> {
		const deviceId = this._readDeviceId();
		if(deviceId === null) {
			if(ENABLE_ENCLAVE) {
				const enclaveResult = await this.verifyEnclave();
				if(!enclaveResult.is_reachable) {
					throw new EnclaveNotReachableError('Secure Enclave not reachable');
				}
			}
			const rmmResult = await this.verifyRmmServer();
			if(!rmmResult.is_reachable) {
				throw new RmmNotReachableError('RMM server not reachable');
			}
			if(rmmResult.is_time_in_sync === false) {
				throw new TimeNotInSyncError('Client time is not in sync with server time');
			}
			console.log('Registering device...');
			const registration = await this._registerDeviceWithRmm();
			console.log(`Registered device ID: ${registration.device_id}`);
			if(
				ENABLE_ENCLAVE
				&& this._readIsRefreshTokenSecured() !== true
			) {
				await this._registerRefreshTokenWithEnclave(registration.device_id);
			}
			return registration.device_id;
		} else if(
			ENABLE_ENCLAVE
			&& this._readIsRefreshTokenSecured() !== true
		) {
			await this._registerRefreshTokenWithEnclave(deviceId);
		}
		return deviceId;
	}

	// #region Polling
	poll(): void {
		if(typeof this._throttle_desired_state === 'undefined') {
			return;
		}
		this._throttle_desired_state();
	}

	async _poll(): Promise<void> {
		await this._readOrRegisterDeviceId();
		if(typeof this._desired_state_event_source === 'undefined') {
			this._desired_state_event_source = await this._createDesiredStateEventSource();
		}
		// Pull desired state.
		await this._pollDesiredState();
		this._cool.online();
		// Push runtime status.
		await this._reportRuntimeStatus();
	}

	async _pollDesiredState(): Promise<void> {
		const {
			desiredState,
			etag,
			lastModified,
		} = await this._desiredState(
			this.desiredStateEtag,
			this.desiredStateLastModified,
		);
		if(desiredState === null) {
			console.log('DEVICE: No change in desired state.');
			this._readDesiredState();
			// Capture the initial state.
			return this._reportRuntimeState();
		}
		console.log('DEVICE: Changed detected.');
		const hasChanged = await this._writeDesiredState(desiredState, etag, lastModified);
		if(hasChanged) {
			return this._reportRuntimeState();
		}
	};
	// #endregion

	// #region Notification
	async _createDesiredStateEventSource(): Promise<DesiredStateEventSource> {
		const sse = await this.createDeviceEventSource();
		sse.addEventListener('message', (_event) => {
			if(typeof this._throttle_desired_state === 'undefined') {
				return;
			}
			this._throttle_desired_state();
		});
		return sse;
	}
	// #endregion

	// #region Tokens
	async revokeRefreshToken(): Promise<void> {
		console.warn('revoking refresh token');
		const tokenSet = this._readTokenSet();
		if(tokenSet) {
			await this._revokeToken(tokenSet.refresh_token);
			this._deleteTokenSet();
		}
	}

	async _accessToken(): Promise<string> {
		const tokenSet = await this._tokenSet();
		if(!tokenSet) {
			throw new Error('No access token');
		}
//		console.log(`access token: ${tokenSet.access_token}`);
		return tokenSet.access_token;
	};

	async _tokenSet(): Promise<TokenSet | null> {
		let tokenSet = this._readTokenSet();
//		console.log(`tokenSet: ${JSON.stringify(tokenSet)}`);
		if(tokenSet === null) {
			tokenSet = await this._tokenSetFromDeviceAuthorizationGrant();
		} else if(tokenSet.expired()) {
			tokenSet = await this._tokenSetFromRefreshTokenGrant(tokenSet);
			// Forward new token to the desired state event source if it exists.
			if(this._desired_state_event_source) {
				this._desired_state_event_source.url = await this.createUrlForDevice(tokenSet.access_token);
			}
			for(const agent of this._agents) {
				const sse = agent.desiredStateEventSource;
				if(!sse) continue;
				sse.url = await this.createUrlForAgent(agent.agentId, tokenSet.access_token);
			}
		} else if(window.dsbunny.rmm.state !== 'authorized') {
			console.log(`Cached token, expires at ${(new Date(tokenSet.expires_at)).toISOString()}`);
			window.dsbunny.rmm.state = 'authorized';
		}
		return tokenSet;
	};

	async _tokenSetFromDeviceAuthorizationGrant(): Promise<TokenSet> {
		window.dsbunny.rmm.state = 'authorizing';
		let tokenSet: TokenSet | null = null;
		do {
			try {
				const handle = await this._startDeviceAuthorizationGrant();
				window.dsbunny.rmm.user_code = handle.user_code;
				window.dsbunny.rmm.verification_uri = handle.verification_uri;
				window.dsbunny.rmm.verification_uri_complete = handle.verification_uri_complete;
				tokenSet = await this._pollTokenSet(handle);
				if(tokenSet) break;
				throw new Error('Token could not be obtained');
			} catch(err: unknown) {
				console.warn(`_tokenSetFromDeviceAuthorizationGrant: ${err}`);
				if(
					err instanceof Error
					&& err.message === 'expired'
				) {
					console.log('Device authorization grant expired, restarting...');
					continue; // Restart device authorization grant if expired.
				}
				throw err;
			}
		} while(tokenSet === null);
		console.log(`Token set, expires at ${(new Date(tokenSet.expires_at)).toISOString()}`);
		this._writeTokenSet(tokenSet);
		window.dsbunny.rmm.state = 'authorized';
		return tokenSet;
	}

	async _pollTokenSet(handle: DeviceFlowHandle): Promise<TokenSet> {
		const tokenSet = await handle.poll();
		return tokenSet;
	};

	async _revokeToken(token: string): Promise<void> {
		const as = await this._authorizationServer();
		const client = await this._client();
		const response = await oauth2.revocationRequest(
			as,
			client,
			oauth2.None(),
			token,
		);
		// Note different ABI to other response handlers.
		const error = await oauth2.processRevocationResponse(response);
		if(typeof error !== 'undefined') {
			console.log('error', error);
			throw new Error(); // Handle OAuth 2.0 response body error
		}
	};

	// HTTP/403 Forbidden
	// {
	//   "error": "invalid_grant",
	//   "error_description": "Unknown or invalid refresh token."
	// }
	async _tokenSetFromRefreshTokenGrant(
		tokenSet: TokenSet,
	): Promise<TokenSet> {
		const as = await this._authorizationServer();
		const client = await this._client();
		const response = await oauth2.refreshTokenGrantRequest(
			as,
			client,
			oauth2.None(),
			tokenSet.refresh_token,
		);
		if(!response.ok) {
			// Detect revoked token by HTTP/403 Forbidden status on access token request.
			if(response.status === 403) {
				throw new RefreshTokenForbiddenError(`HTTP status: ${response.status}`);
			}
			throw new Error(`HTTP status: ${response.status}`);
		}
		const result = await oauth2.processRefreshTokenResponse(
			as,
			client,
			response,
		);
		console.log('result:', result);
		if(typeof result.access_token !== 'string') {
			throw new Error('no access token');
		}
		let expiresAt = 0;
		const jwtPayload = decodeJwt(result.access_token);
		if(jwtPayload
			&& typeof jwtPayload.exp === 'number')
		{
			expiresAt = jwtPayload.exp * 1000;
		}
		const refreshTokenSet = new TokenSet({
			access_token: result.access_token,
			refresh_token: tokenSet.refresh_token, // Keep the same refresh token.
			expires_at: expiresAt,
		});
		return refreshTokenSet;
	}
	// #endregion

	// #region Authorization
	async _startDeviceAuthorizationGrant(): Promise<DeviceFlowHandle> {
		const as = await this._authorizationServer();
		const client = await this._client();
		const parameters = new URLSearchParams();
		parameters.set('scope', RMM_SCOPE);
		parameters.set('audience', RMM_AUDIENCE);
		const response = await oauth2.deviceAuthorizationRequest(
			as,
			client,
			oauth2.None(),
			parameters,
		);
		const result = await oauth2.processDeviceAuthorizationResponse(
			as,
			client,
			response,
		);
		console.log('result', result);
		const handle = new DeviceFlowHandle({
			as,
			client,
			client_auth: oauth2.None(),
			device_code: result.device_code,
			user_code: result.user_code,
			verification_uri: result.verification_uri,
			verification_uri_complete: result.verification_uri_complete,
			expires_in: result.expires_in,
			interval: result.interval ?? 5,
		});
		return handle;
	};

	async _authorizationServer(): Promise<oauth2.AuthorizationServer> {
		const response = await oauth2.discoveryRequest(this.issuer);
		if(!response.ok) {
			throw new Oauth2DiscoveryError(`HTTP status: ${response.status}`);
		}
		const as = await oauth2.processDiscoveryResponse(this.issuer, response);
		return as;
	}

	async _client(): Promise<oauth2.Client> {
		const client: oauth2.Client = {
			client_id: await this.clientId(),
			token_endpoint_auth_method: 'none',
		};
		return client;
	}
	// #endregion

	// #region Registration
	async _registerDeviceWithRmm(): Promise<DeviceRegistration> {
		const request: CreateDeviceRequest = {
			name: 'Kuromi',
			tags: this.tags,
		};
		const url = `${RMM_BASE_URL}/api/v1/devices`;
		const response = await this.fetchProtectedResource(url, {
			method: 'POST',
			headers: {
				'content-type': 'application/json',
			},
			body: JSON.stringify(request),
		});
		if(!response.ok) {
			throw new Error(`HTTP status: ${response.status}`);
		}
		const json = await response.json();
		const result = CreateDeviceResponseSchema.parse(json);
		const registration = result satisfies DeviceRegistration;
		this._writeDeviceId(registration.device_id);
		return registration;
	}

	async _registerRefreshTokenWithEnclave(deviceId: string): Promise<void> {
		try {
			const url = `${ENCLAVE_BASE_URL}/api/v1/enclave/tokens`;
			const request = {
				ref_id: deviceId,
				token: await this._accessToken(),
			}
			const response = await this.fetchProtectedResource(url, {
				method: 'POST',
				headers: {
					'content-type': 'application/json',
				},
				body: JSON.stringify(request),
			});
			if(!response.ok) {
				throw new Error(`HTTP status: ${response.status}`);
			}
			this._writeIsRefreshTokenSecured(true);
		} catch(err: unknown) {
			console.warn(`_registerRefreshTokenWithEnclave: ${
				(err instanceof Error) ? JSON.stringify({
					name: err.name,
					message: err.message,
					stack: err.stack,
					cause: err.cause,
				}) : JSON.stringify({ err })
			}`);
			throw err;
		}
	}
	// #endregion

	// #region Protected Fetch
	async fetchProtectedResource(
		url: string,
		options: RequestInit,
	): Promise<Response> {
		const accessToken = await this._accessToken();
		const response = await fetch(url, {
			method: options.method || 'GET',
			headers: {
				...options.headers,
				'authorization': `Bearer ${accessToken}`,
				'accept': 'application/json',
			},
			mode: 'cors',
			cache: options.cache || 'no-store',
			body: options.body || undefined,
		});
		if(response.status === 401) {
			// TODO: refresh token.
			throw new Error('unauthorized');
		}
		return response;
	}
	// #endregion

	// #region Protected Event Source
	async createDeviceEventSource(): Promise<DesiredStateEventSource> {
		const accessToken = await this._accessToken();
		const urlWithToken = await this.createUrlForDevice(accessToken);
		const eventSource = new DesiredStateEventSource(urlWithToken);
		return eventSource;
	}

	async createAgentEventSource(agentId: string): Promise<DesiredStateEventSource> {
		const accessToken = await this._accessToken();
		const urlWithToken = await this.createUrlForAgent(agentId, accessToken);
		const eventSource = new DesiredStateEventSource(urlWithToken);
		return eventSource;
	}

	async createUrlForDevice(accessToken: string): Promise<string> {
		return `${RMM_BASE_URL}/api/v1/devices/desired-state/events?token=${accessToken}`
	}

	async createUrlForAgent(agentId: string, accessToken: string): Promise<string> {
		return `${RMM_BASE_URL}/api/v1/agents/${agentId}/desired-state/events?token=${accessToken}`
	}
	// #endregion

	// #region Runtime State
	get _nullRuntimeState(): DeviceStateBase {
		return {
			uri: null,
			pull_interval: null,
			push_interval: null,
			min_backoff_interval: null,
			max_backoff_interval: null,
			agent_ids: null,
			is_maintenance: false,
		};
	}

	async _reportRuntimeState(): Promise<void> {
		try {
			const request: UpdateDeviceStateRequest = this._runtimeState();
			const url = `${RMM_BASE_URL}/api/v1/devices/runtime-state`;
			const response = await this.fetchProtectedResource(url, {
				method: 'PUT',
				headers: {
					'content-type': 'application/json',
				},
				body: JSON.stringify(request),
			});
			if(!response.ok) {
				throw new Error(`HTTP status: ${response.status}`);
			}
		} catch(err: unknown) {
			console.warn(`reportRuntimeState: ${
				(err instanceof Error) ? JSON.stringify({
					name: err.name,
					message: err.message,
					stack: err.stack,
					cause: err.cause,
				}) : JSON.stringify({ err })
			}`);
			throw err;
		}
	}

	_runtimeState(): DeviceStateBase {
		if(!this.isActive) {
			return this._nullRuntimeState;
		}
		return {
			uri: this.uri,
			pull_interval: this.pullInterval,
			push_interval: this.pushInterval,
			min_backoff_interval: this.minBackoffInterval,
			max_backoff_interval: this.maxBackoffInterval,
			agent_ids: this.agentIds,
			is_maintenance: this.isMaintenance,
		};
	}
	// #endregion

	// #region Runtime Status
	get _nullRuntimeStatus(): DeviceStatusBase {
		return {
			uri: null,
			user_agent: null,
			device_memory: null,
			hardware_concurrency: null,
			vendor_webgl: null,
			renderer_webgl: null,
			screen_details: null,
			cool: null,
			has_error: false,
			error_stack: null,
		};
	}

	async _reportRuntimeStatus(): Promise<void> {
		try {
			const request: UpdateDeviceStatusRequest = await this._runtimeStatus();
			const url = `${RMM_BASE_URL}/api/v1/devices/runtime-status`;
			const response = await this.fetchProtectedResource(url, {
				method: 'PUT',
				headers: {
					'content-type': 'application/json',
				},
				body: JSON.stringify(request),
			});
			if(!response.ok) {
				throw new Error(`HTTP status: ${response.status}`);
			}
		} catch(err: unknown) {
			console.warn(`reportRuntimeStatus: ${
				(err instanceof Error) ? JSON.stringify({
					name: err.name,
					message: err.message,
					stack: err.stack,
					cause: err.cause,
				}) : JSON.stringify({ err })
			}`);
			throw err;
		}
	}

	async _runtimeStatus(): Promise<DeviceStatusBase> {
		if(!this.isActive) {
			return this._nullRuntimeStatus;
		}
		let screens: DeviceScreenDetailed[] = [];
		try {
			const result = await navigator.permissions.query({
				name: "window-management" as PermissionName,
			});
			if(result.state === 'granted') {
				const screenDetails = await window.getScreenDetails();
				// Premultiply device pixel ratio for convenience.
				screens = screenDetails.screens.map((screen) => ({
					label: screen.label,
					left: Math.round(screen.devicePixelRatio * screen.left),
					top: Math.round(screen.devicePixelRatio * screen.top),
					width: Math.round(screen.devicePixelRatio * screen.width),
					height: Math.round(screen.devicePixelRatio * screen.height),
					devicePixelRatio: screen.devicePixelRatio,
					orientation: {
						type: screen.orientation.type,
						angle: screen.orientation.angle,
					},
					// devicePixelRatio: screen.devicePixelRatio,
					// pixelDepth: screen.pixelDepth,
				}));
			}
		} catch(error) {
			console.warn('window-management permission denied or not supported');
		}
		if(screens.length === 0) {
			// `availLeft` and `availTop` are only available in a secure context.
			const availLeft = ('availLeft' in screen
				&& typeof screen.availLeft === 'number')
				? screen.availLeft : 0;
			const availTop = ('availTop' in screen
				&& typeof screen.availTop === 'number')
				? screen.availTop : 0;
			// Fallback to window.screen.
			screens = [{
				label: 'window',
				left: Math.round(window.devicePixelRatio * availLeft),
				top: Math.round(window.devicePixelRatio * availTop),
				width: Math.round(window.devicePixelRatio * screen.width),
				height: Math.round(window.devicePixelRatio * screen.height),
				devicePixelRatio: window.devicePixelRatio,
				orientation: {
					type: screen.orientation.type,
					angle: screen.orientation.angle,
				},
				// devicePixelRatio: screen.devicePixelRatio,
				// pixelDepth: screen.pixelDepth,
			}];
		}
		const deviceMemory = ('deviceMemory' in navigator
			&& typeof navigator.deviceMemory === 'number')
			? navigator.deviceMemory : 0;
		return {
			uri: this.uri,
			user_agent: navigator.userAgent,
			device_memory: deviceMemory,
			hardware_concurrency: navigator.hardwareConcurrency,
			vendor_webgl: this.vendorWebGL || null,
			renderer_webgl: this.rendererWebGL || null,
			screen_details: {
				screens,
			},
			cool: this._cool.report(),
			has_error: this._has_error,
			error_stack: this._error_stack,
		};
	}

	get startTimestamp(): Date {
		if('timeOrigin' in performance) {
			return new Date(performance.timeOrigin);
		}
		// `performance.timing.navigationStart` is deprecated, but still available in some browsers.
		return new Date((performance as any).timing.navigationStart);
	}
	// #endregion

	// #region Desired State
	override _readDesiredState(): boolean {
		let hasChanged = super._readDesiredState();
		hasChanged = this._readAgentIds() || hasChanged;
		return hasChanged;
	}

	// Note that '|=' is not supported for `boolean` types.
	override async _writeDesiredState(
		desiredState: DeviceState,
		etag: string | null,
		lastModified: string | null,
	): Promise<boolean> {
		let hasChanged = await super._writeDesiredState(desiredState, etag, lastModified);
		hasChanged = this._writeAgentIds(desiredState.agent_ids) || hasChanged;
		return hasChanged;
	}

	async _desiredState(
		etag: string | null,
		lastModified: string | null,
	): Promise<{
		desiredState: DeviceState | null,
		etag: string | null,
		lastModified: string | null,
	}> {
		const headers: Record<string, string> = {};
		if(etag !== null) {
			headers['if-none-match'] = etag;
		}
		if(lastModified !== null) {
			headers['if-modified-since'] = lastModified;
		}
		const url = `${RMM_BASE_URL}/api/v1/devices/desired-state`;
		const response = await this.fetchProtectedResource(url, {
			method: 'GET',
			headers,
			cache: 'no-cache',
		});
		if(!response.ok) {
			if(response.status === 304) {
				return {
					desiredState: null,
					etag,
					lastModified,
				};
			}
			throw new Error(`HTTP status: ${response.status}`);
		}
		const json = await response.json();
		const result = GetDeviceStateResponseSchema.parse(json);
		const desiredState = result satisfies DeviceState;
		return {
			desiredState,
			etag: response.headers.get('etag'),
			lastModified: response.headers.get('last-modified'),
		};
	}

	override async _deleteDesiredState(): Promise<void> {
		await super._deleteDesiredState();
		this._deleteAgentIds();
	}

	override async _onUriChanged(uri: string | null): Promise<void> {
		this._is_active = (uri === BunnyDevice.URN);
	}

	override _onPullIntervalChanged(pullInterval: number | null): void {
		// no-op
	}
	override _onPushIntervalChanged(pushInterval: number | null): void {
		// no-op
	}
	override _onMinBackoffIntervalChanged(minBackoffInterval: number | null): void {
		// no-op
	}
	override _onMaxBackoffIntervalChanged(maxBackoffInterval: number | null): void {
		// no-op
	}
	// #endregion

	// #region Agent Ids
	get agentIds(): Array<string> {
		return (!!this._cached_agent_ids_as_text)
			? JSON.parse(this._cached_agent_ids_as_text)
			: [];
	}

	_readAgentIds(): boolean {
		const agentIdsAsText = this._readKeyString('agentIds');
		if(agentIdsAsText !== this._cached_agent_ids_as_text) {
			this._cached_agent_ids_as_text = agentIdsAsText;
			this._onAgentIdsChanged(JSON.parse(agentIdsAsText || '[]'));
			return true;
		}
		return false;
	}

	_writeAgentIds(agentIds: Array<string> | null): boolean {
		const agentIdsAsText = JSON.stringify(agentIds);
		if(agentIdsAsText === this._cached_agent_ids_as_text) {
			return false;
		}
		this._cached_agent_ids_as_text = agentIdsAsText;
		this._writeKeyString('agentIds', agentIdsAsText);
		this._onAgentIdsChanged(agentIds || []);
		return true;
	}

	_deleteAgentIds(): void {
		this._writeAgentIds(null);
	}

	_onAgentIdsChanged(desiredAgentIds: Array<string>): void {
		let shutdown_count = 0;
		let startup_count = 0;
		for(const agent of this._agents) {
			if(!desiredAgentIds.includes(agent.agentId)) {
				this._deleteAgent(agent);
				shutdown_count++;
			}
		}
		if(shutdown_count > 0) {
			console.log(`shutdown ${shutdown_count} agents`);
		}

		if(!desiredAgentIds) {
			return;
		}
		for(const desiredAgentId of desiredAgentIds) {
			if(!this._hasAgent(desiredAgentId)) {
				const agent = this._createAgent(desiredAgentId);
				agent.poll();
				startup_count++;
			}
		}
		if(startup_count > 0) {
			console.log(`startup ${startup_count} agents`);
		}
	}

	get _agents(): IterableIterator<BunnyAgent> {
		return window.dsbunny.rmm.agents.values();
	}

	_createAgent(agentId: string): BunnyAgent {
		const implFactory: BunnyAgentImplFactory = this._createAgentImpl.bind(this);
		const agent = new BunnyAgent({
			agentId,
			implFactory,
		});
		window.dsbunny.rmm.agents.set(agentId, agent);
		return agent;
	}

	async _createAgentImpl(
		agent: BunnyAgent,
		uri: string | null,
	): Promise<BunnyAgentImpl | undefined> {
		switch(uri) {
		case BunnyAgentRenderer.URN:
			return await BunnyAgentRenderer.createAgent(agent);
		default:
			return undefined;
		}
	}

	_deleteAgentById(agentId: string): void {
		const agent = window.dsbunny.rmm.agents.get(agentId);
		if(agent) {
			this._deleteAgent(agent);
		}
	}

	_deleteAgent(agent: BunnyAgent): void {
		agent.destroy();
		window.dsbunny.rmm.agents.delete(agent.agentId);
	}

	_hasAgent(agentId: string): boolean {
		return window.dsbunny.rmm.agents.has(agentId);
	}

	_getAgent(agentId: string): BunnyAgent | undefined {
		return window.dsbunny.rmm.agents.get(agentId);
	}
	// #endregion

	// #region Maintenance Mode
	get isMaintenance(): boolean {
		return this._cached_is_maintenance || false;
	}

	_readIsMaintenance(): boolean {
		const isMaintenance = this._readKeyBoolean('isMaintenance');
		if(isMaintenance === this._cached_is_maintenance) {
			return false;
		}
		this._cached_is_maintenance = isMaintenance;
		this._onIsMaintenanceChanged(isMaintenance);
		return true;
	}

	_writeIsMaintenance(isMaintenance: boolean): boolean {
		if(isMaintenance === this._cached_is_maintenance) {
			return false;
		}
		this._cached_is_maintenance = isMaintenance;
		this._writeKeyBoolean('isMaintenance', this._cached_is_maintenance = isMaintenance);
		this._onIsMaintenanceChanged(isMaintenance);
		return true;
	}

	_onIsMaintenanceChanged(isMaintenance: boolean): void {
		if(isMaintenance) {
			console.log('Device is in maintenance mode');
		} else {
			console.log('Device is not in maintenance mode');
		}
	}

	_deleteIsMaintenance(): void {
		this._writeIsMaintenance(false);
	}
	// #endregion

	// #region Access Token
	_readAccessToken(): string | null {
		if(typeof this._cached_access_token !== 'undefined') {
			return this._cached_access_token;
		}
		const accessToken = this._readKeyString('accessToken');
		return this._cached_access_token = accessToken;
	}

	_writeAccessToken(accessToken: string | null): boolean {
		if(accessToken === this._cached_access_token) {
			return false;
		}
		this._writeKeyString('accessToken', this._cached_access_token = accessToken);
		return true;
	}

	_deleteAccessToken(): void {
		this._writeAccessToken(null);
	}
	// #endregion

	// #region Refresh Token
	_readRefreshToken(): string | null {
		if(typeof this._cached_refresh_token !== 'undefined') {
			return this._cached_refresh_token;
		}
		const refreshToken = this._readKeyString('refreshToken');
		return this._cached_refresh_token = refreshToken;
	}

	_writeRefreshToken(refreshToken: string | null): boolean {
		if(refreshToken === this._cached_refresh_token) {
			return false;
		}
		this._writeKeyString('refreshToken', this._cached_refresh_token = refreshToken);
		return true;
	}

	_deleteRefreshToken(): void {
		this._writeRefreshToken(null);
	}

	_readIsRefreshTokenSecured(): boolean | null {
		if(typeof this._cached_is_refresh_token_secured !== 'undefined') {
			return this._cached_is_refresh_token_secured;
		}
		const isRefreshTokenSecured = this._readKeyBoolean('isRefreshTokenSecured');
		return this._cached_is_refresh_token_secured = isRefreshTokenSecured;
	}

	_writeIsRefreshTokenSecured(isRefreshTokenSecured: boolean): boolean {
		if(isRefreshTokenSecured === this._cached_is_refresh_token_secured) {
			return false;
		}
		this._cached_is_refresh_token_secured = isRefreshTokenSecured;
		this._writeKeyBoolean('isRefreshTokenSecured', this._cached_is_refresh_token_secured = isRefreshTokenSecured);
		return true;
	}

	_deleteIsRefreshTokenSecured(): void {
		this._writeIsRefreshTokenSecured(false);
	}
	// #endregion

	// #region Token Set
	_readTokenSet(): TokenSet | null {
		const accessToken = this._readAccessToken();
		const refreshToken = this._readRefreshToken();
		if(accessToken === null || refreshToken === null) {
			return null;
		}
		let expiresAt = 0;
		const jwtPayload = decodeJwt(accessToken);
		if(jwtPayload
			&& typeof jwtPayload.exp === 'number')
		{
			expiresAt = jwtPayload.exp * 1000;
		}
		return new TokenSet({
			access_token: accessToken,
			refresh_token: refreshToken,
			expires_at: expiresAt,
		});
	}

	_writeTokenSet(tokenSet: TokenSet): boolean {
		let hasChanged = this._writeAccessToken(tokenSet.access_token);
		hasChanged = this._writeRefreshToken(tokenSet.refresh_token) || hasChanged;
		return hasChanged;
	}

	_deleteTokenSet(): void {
		this._deleteAccessToken();
		this._deleteRefreshToken();
	}
	// #endregion

	// #region Device Id
	_readDeviceId(): string | null {
		if(typeof this._cached_device_id !== 'undefined') {
			return this._cached_device_id;
		}
		const deviceId = this._readKeyString('deviceId');
		return this._cached_device_id = deviceId;
	}

	_writeDeviceId(deviceId: string | null): boolean {
		if(deviceId === this._cached_device_id) {
			return false;
		}
		this._writeKeyString('deviceId', this._cached_device_id = deviceId);
		return true;
	}

	_deleteDeviceId(): void {
		this._writeDeviceId(null);
	}
	// #endregion
}
